import { Grid2, Typography, Box, ToggleButton, ToggleButtonGroup, Chip, } from "@mui/material"
import { memo, useMemo, useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import Table from "../components/Table"
import RefreshIcon from '@mui/icons-material/Refresh';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { stringAToZInsensitive } from "@iamnapo/sort";
import { isFuzzyMatch } from "../utils/index.js";
import { getInvitedUsers, deleteInvitedUser, getUserRole, getOrganizations, resendInvitation, getKentros } from "../api/index.js";
import Spinner from "../components/Spinner.js";
import { useSnackbar } from "../utils/index.js";
import AreYouSurePopup from "../components/popups/AreYouSurePopup.js";
import { dateRangeFilterMethod } from "../utils/date-filter.js";

const useStyles = makeStyles((theme) => ({
    form_title: {
        color: theme.palette.primary.main,
        display: "flex",
        alignItems: "center"
    },
    container: {
        width: "65%"
    }
}));

const InvitedUsers = () => {
    const classes = useStyles();
    const [invitedUsers, setInvitedUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [invitationToBeDeleted, setInvitationToBeDeleted] = useState(null);
    const [fetchedOrganizations, setFetchedOrganizations] = useState([]);
    const [fetchedKentros, setFetchedKentros] = useState([]);

    const { error, success } = useSnackbar();

    const fetchInvitedUsers = async () => {
        setIsLoading(true)
        const [{ success: successCode, users }, , { organizations }, { kentros }] = await Promise.all([
            getInvitedUsers(),
            getUserRole(),
            getOrganizations(),
            getKentros(),
        ]);

        if (successCode) {
            setInvitedUsers(users)
        }

        setFetchedOrganizations(organizations)
        setFetchedKentros(kentros)
        setIsLoading(false)
    }

    useEffect(() => {
        fetchInvitedUsers();
    }, []);

    const handleResendInvitation = async (user) => {
        setIsLoading(true)
        try {
            const { success: successCode, message } = await resendInvitation(user.email);

            if (successCode) {
                success(message);
            } else {
                error(message);
            }
        } catch { /* empty */ }

        fetchInvitedUsers();
        setIsLoading(false);
    }

    const handleCopyToClipboard = (value) => {
        if (value.token && value.email) {
            navigator.clipboard.writeText(`${window.location.origin}/complete-registration?token=${value.token}&email=${encodeURIComponent(value.email)}`).then(() => {
                success("Ο σύνδεσμος αντιγράφηκε στο πρόχειρο.");
            }).catch(() => {
                error("Σφάλμα κατά την αντιγραφή συνδέσμου.");
            });
        }
    };

    const handleDeleteInvitedUser = async () => {
        setIsLoading(true);

        const { success: successCode, message } = await deleteInvitedUser(invitationToBeDeleted)

        if (successCode) {
            success(message);
        } else {
            error(message);
        }

        setIsPopupOpen(false);
        setInvitationToBeDeleted(null);
        setIsLoading(false);
        fetchInvitedUsers();
    }

    const tableColumns = useMemo(() => [
        {
            Header: <Typography variant="h8">{""}</Typography>,
            accessor: "",
            filterable: false,
            sortable: false,
            maxWidth: 200,
            minWidth: 180,
            Cell: ({ value }) => (
                <ToggleButtonGroup exclusive size="small" aria-label="actions">
                    <ToggleButton
                        value="resend"
                        title="Επαναποστολή Πρόσκλησης"
                        aria-label="resend"
                        sx={{ borderColor: "primary.main" }}
                        onClick={() => handleResendInvitation(value)}
                    >
                        <RefreshIcon color="primary.main" />
                    </ToggleButton>
                    <ToggleButton
                        value="copy"
                        title="Αντιγραφή Συνδέσμου Πρόσκλησης"
                        aria-label="copy"
                        sx={{ borderColor: "primary.main" }}
                        onClick={() => handleCopyToClipboard(value)}
                    >
                        <FileCopyIcon color="primary.main" />
                    </ToggleButton>
                    <ToggleButton
                        value="delete"
                        title="Διαγραφή Πρόσκλησης"
                        aria-label="delete"
                        sx={{ borderColor: "primary.main" }}
                        onClick={() => {
                            setIsPopupOpen(true);
                            setInvitationToBeDeleted(value.email);
                        }}
                    >
                        <DeleteIcon color="primary.main" />
                    </ToggleButton>
                </ToggleButtonGroup>
            ),
        },
        {
            Header: <Typography id="email_header" variant="h8">{"Email"}</Typography>,
            accessor: "email",
            id: "email",
            filterable: true,
            maxWidth: 250,
            minWidth: 200,
            sortMethod: (value1, value2) => stringAToZInsensitive()(value1, value2),
            filterMethod: ({ id, value }, row) => isFuzzyMatch(row[id], value),
            Cell: ({ value }) => (
                <Box sx={{ display: "flex", ml: 1, alignItems: "center", justifyContent: "center" }}>
                    <Typography>
                        {value}
                    </Typography>
                </Box>
            ),
        },
        {
            Header: <Typography id="role_header" variant="h8">{"Ρόλος"}</Typography>,
            accessor: "role",
            id: "role",
            filterable: true,
            maxWidth: 250,
            minWidth: 200,
            useDropdownFilter: true,
            filterOptions: [
                { value: "USER", label: "USER" },
                { value: "ADMIN", label: "ADMIN" },
                { value: "KENTRO_USER", label: "KENTRO_USER" },
            ],
            sortMethod: (value1, value2) => stringAToZInsensitive()(value1, value2),
            filterMethod: ({ id, value }, row) => {
                return value.length === 0 || value.includes(row[id]);
            },
            Cell: ({ value }) => {
                const chipColors = {
                    ADMIN: { backgroundColor: "error.main", color: "white" },
                    USER: { backgroundColor: "secondary.main", color: "third.main" },
                    KENTRO_USER: { backgroundColor: "primary.main", color: "white" },
                };
                const sx = chipColors[value] ?? { backgroundColor: "secondary.main", color: "third.main" };
                return (
                    <Box sx={{ display: "flex", ml: 1, alignItems: "center", justifyContent: "center" }}>
                        <Chip label={value} sx={sx} />
                    </Box>
                );
            },
        },
        {
            Header: <Typography id="affiliation_header" variant="h8">{"Οργάνωση / Κέντρο"}</Typography>,
            accessor: (row) => row,
            id: "affiliation",
            filterable: false,
            maxWidth: 300,
            minWidth: 200,
            Cell: ({ value: row }) => {
                let displayName = "—";
                if (row.kentroName) {
                    // Server-enriched kentro name
                    displayName = `${row.kentroName} (Κέντρο)`;
                } else if (row.kentro_id != null) {
                    // Client-side fallback
                    const k = fetchedKentros.find((k) => k.kentro_id === row.kentro_id);
                    displayName = k ? `${k.name} (Κέντρο)` : `Κέντρο #${row.kentro_id}`;
                } else if (row.orgId) {
                    displayName = fetchedOrganizations.find((org) => org.orgId === row.orgId)?.name ?? row.orgId;
                }
                return (
                    <Box sx={{ display: "flex", ml: 1, alignItems: "center", justifyContent: "center", textAlign: "center" }}>
                        <Typography sx={{ fontSize: "0.75rem", whiteSpace: "normal", wordBreak: "break-word" }}>
                            {displayName}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            Header: <Typography id="createdAt_header" variant="h8">{"Ημ/νια πρόσκλησης"}</Typography>,
            accessor: "createdAt",
            id: "createdAt",
            filterable: true,
            maxWidth: 300,
            minWidth: 230,
            useDatePickerFilter: true,
            sortMethod: (value1, value2) => stringAToZInsensitive()(value1, value2),
            filterMethod: dateRangeFilterMethod,
            Cell: ({ value }) => (
                <Box sx={{ display: "flex", ml: 1, alignItems: "center", justifyContent: "center" }}>
                    <Typography>
                        {new Date(value).toLocaleDateString("el-GR")}
                    </Typography>
                </Box>
            ),
        },
    ])
    return (
        <>
            <Spinner open={isLoading} />
            <Grid2 container className={classes.container} direction="column" sx={{ marginTop: "3rem", marginLeft: "10rem" }}>
                <Typography variant="h5" className={classes.form_title}>Προσκεκλημένοι Χρήστες</Typography>
                <Typography variant="subtitle1" className={classes.form_title}>Παρακάτω φαίνονται όλοι οι προσκεκλημενοι χρήστες της πλατφόρμας:</Typography>
                <Table
                    data={invitedUsers}
                    noDataText={"Δεν βρέθηκαν εγγραφές!"}
                    columns={tableColumns}
                    defaultSorted={[{ id: "name", desc: true }]}
                />
                <AreYouSurePopup
                    width={"900px"}
                    isOpen={isPopupOpen}
                    title="Ακύρωση Πρόσκλησης"
                    titleColor="black"
                    onClose={() => setIsPopupOpen(false)}
                    innerText={`Είστε σίγουροι ότι θέλετε να ακυρώσετε την πρόσκληση του/της "${invitationToBeDeleted}";`}
                    declineText="Πίσω"
                    onDecline={() => setIsPopupOpen(false)}
                    acceptText="Ναι, Ακύρωση Πρόσκλησης"
                    onAccept={() => handleDeleteInvitedUser()}
                />
            </Grid2>
        </>
    );
}

export default memo(InvitedUsers);